import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  BadgeCheck,
  FileText,
  FolderOpen,
  Loader2,
  QrCode,
  Rocket,
} from "lucide-react";
import TemplateSvgPreview from "../../../pages/CertificateOnboarding/configure/TemplateSvgPreview";
import QrCodePreviewFromOptions from "../../../pages/CertificateOnboarding/configure/QrCodePreviewFromOptions";
import CertificateOnboardingScopePicker from "./CertificateOnboardingScopePicker";
import { CARD_CLASS } from "../utils/styles";
import EntityConstants from "../../../configs/EntityConstants";
import { GET } from "../../../service/CRUDService";
import { getCredentialFormatLabel } from "../utils/issuerProject";

function isSuccessfulApiResponse(response, data) {
  if (!response?.ok) return false;
  if (data?.status_code != null && Number(data.status_code) !== 200) {
    return false;
  }
  return true;
}

function getApiErrorMessage(data, fallback) {
  return data?.message || data?.error || fallback;
}

function formatFieldLabel(key) {
  return key
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, (char) => char.toUpperCase())
    .trim();
}

function DetailItem({ label, value, mono = false }) {
  if (!value) return null;

  return (
    <div className="min-w-0">
      <dt className="text-xs font-medium text-cfGlobalText mb-1">{label}</dt>
      <dd
        className={`text-sm text-cfHeadingText break-words ${
          mono ? "font-mono text-xs" : ""
        }`}
      >
        {value}
      </dd>
    </div>
  );
}

function formatDefaultValue(value, type) {
  if (value === undefined || value === null || value === "") return "—";
  if (type === "file") {
    const str = String(value);
    if (str.length > 40) return `${str.slice(0, 40)}…`;
    return str === "base64 string" ? "—" : str;
  }
  return String(value);
}

function SubjectFieldRow({ field, t }) {
  const metaParts = [
    field.format &&
      t("certificateOnboarding:complete_field_format", {
        value: field.format,
      }),
    field.max_characters &&
      t("certificateOnboarding:complete_field_max_chars", {
        value: field.max_characters,
      }),
    field.max_size &&
      t("certificateOnboarding:complete_field_max_size", {
        value: field.max_size,
      }),
    field.number_of_lines &&
      t("certificateOnboarding:complete_field_lines", {
        value: field.number_of_lines,
      }),
  ].filter(Boolean);

  return (
    <div className="py-3.5 border-b border-cfCardBorder last:border-b-0">
      <div className="flex flex-wrap items-start justify-between gap-2 mb-2">
        <div className="min-w-0">
          <p className="text-sm font-medium text-cfHeadingText">
            {field.label}
          </p>
          <p className="text-xs text-cfGlobalText font-mono mt-0.5">
            {field.key}
          </p>
        </div>
        <div className="flex flex-wrap gap-1.5 shrink-0">
          <span className="text-xs px-2 py-0.5 rounded-full bg-cfSecondary text-cfGlobalText border border-cfCardBorder capitalize">
            {field.type}
          </span>
          {field.required ? (
            <span className="text-xs px-2 py-0.5 rounded-full bg-cfPrimaryMuted text-cfPrimary border border-cfPrimaryBorder">
              {t("certificateOnboarding:complete_required")}
            </span>
          ) : (
            <span className="text-xs px-2 py-0.5 rounded-full bg-cfSecondary text-cfGlobalText border border-cfCardBorder">
              {t("certificateOnboarding:complete_optional")}
            </span>
          )}
        </div>
      </div>
      <p className="text-sm text-cfHeadingText">
        <span className="text-cfGlobalText">
          {t("certificateOnboarding:complete_default_value")}:{" "}
        </span>
        {formatDefaultValue(field.default, field.type)}
      </p>
      {metaParts.length > 0 && (
        <p className="text-xs text-cfGlobalText mt-1.5">
          {metaParts.join(" · ")}
        </p>
      )}
    </div>
  );
}

function CategoryCard({ category, selected, onSelect, disabled = false }) {
  return (
    <button
      type="button"
      id={`cert-onboarding-category-${category.category_id}`}
      onClick={() => onSelect(category)}
      disabled={disabled}
      className={`text-left p-4 rounded-xl border-2 transition-all duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-cfPrimary disabled:cursor-not-allowed disabled:opacity-60 ${
        selected
          ? "border-cfPrimary bg-cfPrimaryMuted shadow-sm"
          : "border-cfCardBorder bg-cfSecondary hover:border-cfPrimary/40 hover:bg-cfCardHover"
      }`}
    >
      <p className="text-sm font-medium text-cfHeadingText leading-snug">
        {category.name}
      </p>
      <p className="text-xs font-mono text-cfGlobalText mt-1.5">
        {category.category_id}
      </p>
    </button>
  );
}

function TemplateIdChip({ id, primary = false }) {
  return (
    <span
      className={`inline-flex items-center px-3 py-1.5 rounded-lg text-xs font-mono border ${
        primary
          ? "bg-cfPrimaryMuted text-cfPrimary border-cfPrimaryBorder font-semibold"
          : "bg-cfSecondary text-cfHeadingText border-cfCardBorder"
      }`}
    >
      {id}
    </span>
  );
}

function CertificateCompleteStep({
  createFormData,
  credentialFormat,
  onboardingScope,
  onScopeChange,
  selectedCategory,
  onCategorySelect,
  onboardResult,
  isOnboarded = false,
  t,
  certificatePreview,
}) {
  const [categories, setCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [categoriesError, setCategoriesError] = useState(null);

  const credentialFormatLabel = useMemo(
    () => getCredentialFormatLabel(credentialFormat, t),
    [credentialFormat, t],
  );

  const scopeLabel = useMemo(() => {
    if (!onboardingScope) return "";
    if (onboardingScope.type === "general") {
      return t("certificateOnboarding:complete_scope_general");
    }
    return onboardingScope.label || onboardingScope.code || "";
  }, [onboardingScope, t]);

  const displayTemplateIds = useMemo(() => {
    if (!onboardResult) return [];
    const ids = onboardResult.template_ids || [];
    const primary = onboardResult.template_id;
    const unique = [...new Set([primary, ...ids].filter(Boolean))];
    return unique;
  }, [onboardResult]);

  const fetchCategories = useCallback(async () => {
    if (!onboardingScope) return;

    setCategoriesLoading(true);
    setCategoriesError(null);

    try {
      const categoriesUrl =
        !onboardingScope || onboardingScope.type === "general"
          ? EntityConstants.CERTIFICATE_ONBOARDING_CATEGORIES
          : `${EntityConstants.CERTIFICATE_ONBOARDING_CATEGORIES}?${new URLSearchParams(
              {
                code: onboardingScope.code || "",
              },
            ).toString()}`;
      const response = await GET(categoriesUrl);
      const data = await response.json();

      if (!isSuccessfulApiResponse(response, data)) {
        setCategories([]);
        setCategoriesError(
          getApiErrorMessage(
            data,
            t("certificateOnboarding:complete_category_failed"),
          ),
        );
        return;
      }

      setCategories(Array.isArray(data?.data) ? data.data : []);
    } catch (error) {
      console.error("Failed to load categories", error);
      setCategories([]);
      setCategoriesError(t("certificateOnboarding:complete_category_failed"));
    } finally {
      setCategoriesLoading(false);
    }
  }, [onboardingScope, t]);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  const subjectFields = useMemo(() => {
    const properties = createFormData?.credential_subject_properties_json || {};
    const schema = createFormData?.sunbirdrc_schema_json?.schema?.schema || {};
    const requiredSet = new Set(schema.required || []);
    const schemaProperties = schema.properties || {};

    return Object.entries(properties).map(([key, field]) => ({
      key,
      label: schemaProperties[key]?.description || formatFieldLabel(key),
      type: field.type || "string",
      default: field.default,
      format: field.format,
      max_characters: field.max_characters,
      max_size: field.max_size,
      number_of_lines: field.number_of_lines,
      required: requiredSet.has(key),
    }));
  }, [createFormData]);

  const qrPreviewData = useMemo(() => {
    const properties = createFormData?.credential_subject_properties_json || {};
    const payload = {};

    Object.entries(properties).forEach(([key, field]) => {
      if (!field || field.default === undefined || field.default === null) {
        return;
      }
      if (field.type === "file") {
        payload[key] = "[file]";
        return;
      }
      payload[key] = field.default;
    });

    return JSON.stringify(payload, null, 0);
  }, [createFormData?.credential_subject_properties_json]);

  return (
    <div id="cert-onboarding-complete-step" className="mx-auto space-y-5">
      <div
        className={`${CARD_CLASS} px-4 py-4 sm:px-6 sm:py-5 border-l-4 border-l-cfPrimary bg-cfPrimaryMuted`}
      >
        <div className="flex items-start gap-3">
          <div className="shrink-0 w-10 h-10 rounded-full bg-cfPrimaryMuted flex items-center justify-center">
            <BadgeCheck
              className="w-5 h-5 text-cfPrimary"
              strokeWidth={2}
              aria-hidden
            />
          </div>
          <div className="min-w-0 flex-1 space-y-2">
            <h2 className="text-base font-semibold text-cfHeadingText">
              {t("certificateOnboarding:complete_success_title")}
            </h2>
            <p className="text-sm text-cfGlobalText leading-relaxed">
              {t("certificateOnboarding:complete_success_subtitle")}
            </p>
            {createFormData?.transaction_id && (
              <span className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full bg-cfSecondary border border-cfCardBorder text-cfHeadingText">
                <span className="text-cfGlobalText">
                  {t("certificateOnboarding:transaction_id_label")}
                </span>
                <span className="font-mono font-medium">
                  {createFormData.transaction_id}
                </span>
              </span>
            )}
          </div>
        </div>
      </div>

      {isOnboarded && onboardResult && (
        <div
          id="cert-onboarding-onboarded-success"
          className={`${CARD_CLASS} px-4 py-4 sm:px-6 sm:py-5 border-l-4 border-l-cfPrimary bg-cfPrimaryMuted`}
        >
          <div className="flex items-start gap-3">
            <div className="shrink-0 w-10 h-10 rounded-full bg-cfPrimaryMuted flex items-center justify-center">
              <BadgeCheck
                className="w-5 h-5 text-cfPrimary"
                strokeWidth={2}
                aria-hidden
              />
            </div>
            <div className="min-w-0 flex-1 space-y-4">
              <div>
                <h2 className="text-base font-semibold text-cfHeadingText">
                  {t("certificateOnboarding:complete_onboarded_title")}
                </h2>
                <p className="text-sm text-cfGlobalText mt-1 leading-relaxed">
                  {t("certificateOnboarding:complete_onboarded_subtitle")}
                </p>
              </div>

              {onboardResult.template_id && (
                <div>
                  <p className="text-xs font-medium text-cfGlobalText mb-2">
                    {t("certificateOnboarding:complete_template_id_label")}
                  </p>
                  <TemplateIdChip id={onboardResult.template_id} primary />
                </div>
              )}

              {displayTemplateIds.length > 0 && (
                <div>
                  <p className="text-xs font-medium text-cfGlobalText mb-2">
                    {t("certificateOnboarding:complete_template_ids_label")}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {displayTemplateIds.map((id) => (
                      <TemplateIdChip
                        key={id}
                        id={id}
                        primary={id === onboardResult.template_id}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <div className={`${CARD_CLASS} p-4 sm:p-6`}>
        <h3 className="text-sm font-semibold text-cfHeadingText mb-4">
          {t("certificateOnboarding:complete_certificate_details")}
        </h3>
        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <DetailItem
            label={t("certificateOnboarding:certificate_name_label")}
            value={createFormData?.certificate_name}
          />
          <DetailItem
            label={t("certificateOnboarding:folder_name_label")}
            value={createFormData?.entity_name}
          />
          {credentialFormatLabel && (
            <DetailItem
              label={t("certificateOnboarding:credential_format_label")}
              value={credentialFormatLabel}
            />
          )}
          <div className="sm:col-span-2">
            <DetailItem
              label={t("certificateOnboarding:certificate_description_label")}
              value={createFormData?.certificate_description}
            />
          </div>
          <DetailItem
            label={t("certificateOnboarding:complete_template_form_id")}
            value={createFormData?.template_form_id}
            mono
          />
          <DetailItem
            label={t("certificateOnboarding:complete_subject_form_id")}
            value={createFormData?.subject_form_id}
            mono
          />
        </dl>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 items-start">
        {certificatePreview || (
          <TemplateSvgPreview
            templateSvgUrl={createFormData?.template_svg_url}
            previewSvgUrl={createFormData?.preview_svg_url}
            t={t}
          />
        )}

        <div className={`${CARD_CLASS} p-4 sm:p-6`}>
          <div className="flex items-center gap-2 mb-4">
            <QrCode className="w-4 h-4 text-cfPrimary" aria-hidden />
            <h3 className="text-sm font-semibold text-cfHeadingText">
              {t("certificateOnboarding:complete_qr_preview_title")}
            </h3>
          </div>
          <p className="text-xs text-cfGlobalText mb-4 leading-relaxed">
            {t("certificateOnboarding:complete_qr_preview_hint")}
          </p>
          <div className="flex flex-col sm:flex-row items-center gap-5">
            <div className="rounded-xl border border-cfCardBorder bg-cfSecondary p-3 shrink-0">
              <QrCodePreviewFromOptions
                qrCodeOptions={createFormData?.qr_code_options}
                data={qrPreviewData}
              />
            </div>
            <div className="flex-1 w-full min-w-0 space-y-2">
              {subjectFields
                .filter(
                  (field) =>
                    field.default !== undefined && field.type !== "file",
                )
                .slice(0, 6)
                .map((field) => (
                  <div
                    key={field.key}
                    className="flex justify-between gap-3 text-xs border-b border-cfCardBorder/60 pb-1.5 last:border-0"
                  >
                    <span className="text-cfGlobalText truncate">
                      {field.key}
                    </span>
                    <span className="text-cfHeadingText font-medium truncate max-w-[50%] text-right">
                      {formatDefaultValue(field.default, field.type)}
                    </span>
                  </div>
                ))}
              {subjectFields.length > 6 && (
                <p className="text-xs text-cfGlobalText pt-1">
                  {t("certificateOnboarding:complete_qr_more_fields", {
                    count: subjectFields.length - 6,
                  })}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className={`${CARD_CLASS} p-4 sm:p-6`}>
        <div className="flex items-center gap-2 mb-1">
          <FileText className="w-4 h-4 text-cfPrimary" aria-hidden />
          <h3 className="text-sm font-semibold text-cfHeadingText">
            {t("certificateOnboarding:complete_subject_fields_title")}
          </h3>
        </div>
        <p className="text-xs text-cfGlobalText mb-4 leading-relaxed">
          {t("certificateOnboarding:complete_subject_fields_hint")}
        </p>

        {subjectFields.length === 0 ? (
          <p className="text-sm text-cfGlobalText">
            {t("certificateOnboarding:complete_no_subject_fields")}
          </p>
        ) : (
          <div className="divide-y divide-cfCardBorder">
            {subjectFields.map((field) => (
              <SubjectFieldRow key={field.key} field={field} t={t} />
            ))}
          </div>
        )}
      </div>

      <div
        className={`${CARD_CLASS} p-4 sm:p-6 border-l-4 border-l-cfPrimary`}
        id="cert-onboarding-finalize-section"
      >
        <div className="flex items-start gap-3 mb-5">
          <div className="shrink-0 w-10 h-10 rounded-full bg-cfPrimaryMuted flex items-center justify-center">
            <Rocket className="w-5 h-5 text-cfPrimary" aria-hidden />
          </div>
          <div className="min-w-0">
            <h3 className="text-base font-semibold text-cfHeadingText">
              {t("certificateOnboarding:complete_finalize_title")}
            </h3>
            <p className="text-sm text-cfGlobalText mt-1 leading-relaxed">
              {isOnboarded
                ? t("certificateOnboarding:complete_onboarded_finalize_hint")
                : t("certificateOnboarding:complete_finalize_subtitle")}
            </p>
          </div>
        </div>

        <div className="space-y-6">
          <div>
            <div className="flex items-center gap-2 mb-3">
              <FolderOpen className="w-4 h-4 text-cfPrimary" aria-hidden />
              <h4 className="text-sm font-semibold text-cfHeadingText">
                {t("certificateOnboarding:scope_title")}
              </h4>
            </div>
            <CertificateOnboardingScopePicker
              t={t}
              value={onboardingScope}
              onChange={onScopeChange}
              disabled={isOnboarded}
            />
          </div>

          {onboardingScope && (
            <div className="pt-2 border-t border-cfCardBorder">
              {isOnboarded && (
                <div className="mb-4 flex flex-wrap gap-2">
                  {scopeLabel && (
                    <span className="inline-flex items-center text-xs px-2.5 py-1 rounded-full bg-cfSecondary border border-cfCardBorder text-cfHeadingText">
                      {scopeLabel}
                    </span>
                  )}
                  {selectedCategory?.name && (
                    <span className="inline-flex items-center text-xs px-2.5 py-1 rounded-full bg-cfPrimaryMuted text-cfPrimary border border-cfPrimaryBorder">
                      {selectedCategory.name}
                    </span>
                  )}
                </div>
              )}
              <h4 className="text-sm font-semibold text-cfHeadingText mb-1">
                {t("certificateOnboarding:complete_category_title")}
              </h4>
              <p className="text-xs text-cfGlobalText mb-4 leading-relaxed">
                {t("certificateOnboarding:complete_category_hint")}
              </p>

              {categoriesLoading ? (
                <div
                  id="cert-onboarding-categories-loading"
                  className="flex items-center justify-center gap-2 py-10 text-sm text-cfGlobalText"
                >
                  <Loader2 className="w-4 h-4 animate-spin text-cfPrimary" />
                  {t("certificateOnboarding:complete_category_loading")}
                </div>
              ) : categoriesError ? (
                <div className="text-center py-8 space-y-3">
                  <p className="text-sm text-cfStatusBlockedText">
                    {categoriesError}
                  </p>
                  <button
                    type="button"
                    onClick={fetchCategories}
                    className="text-sm font-medium text-cfPrimary hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-cfPrimary rounded-sm"
                  >
                    {t("certificateOnboarding:complete_category_retry")}
                  </button>
                </div>
              ) : categories.length === 0 ? (
                <p className="text-sm text-cfGlobalText text-center py-8">
                  {t("certificateOnboarding:complete_category_empty")}
                </p>
              ) : (
                <>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {categories.map((category) => (
                      <CategoryCard
                        key={category.category_id}
                        category={category}
                        selected={
                          selectedCategory?.category_id === category.category_id
                        }
                        onSelect={onCategorySelect}
                        disabled={isOnboarded}
                      />
                    ))}
                  </div>
                  {selectedCategory && (
                    <p className="text-xs text-cfGlobalText mt-4">
                      {t("certificateOnboarding:complete_category_selected")}:{" "}
                      <span className="font-medium text-cfHeadingText">
                        {selectedCategory.name}
                      </span>
                    </p>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default CertificateCompleteStep;
